import { Box, Button, IconButton, Typography, Tooltip } from "@mui/material";
import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import type { WorkflowRunStoreHandle } from "../store/createWorkflowRunStore";

import { useHistory } from "react-router-dom";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { NucToast } from "src/reusable/components/Toast";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFileUploadWithProgress } from "src/reusable/hooks/useFileUploadWithProgress";
import { ACCEPTED_FILE_TYPES } from "src/constants";
import type {
  FileUploadState,
  UploadStatus,
  ProcessingStatus as ProcessingStatusEnum,
} from "src/reusable/types/fileUploadWithProgress";
import DatasetSelectorModal from "src/reusable/Chat/PromptInput/DatasetSelectorModal";
import ArtifactSelectorModal, {
  type EnrichedArtifact,
  type Dataset as ArtifactSelectorDataset,
} from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import {
  useProcessingStatusBySdrIds,
  selectedArtifactProcessingState,
  getArtifactsBySdrId,
  type ProcessingStatus,
} from "../services/ArtifactEnrichmentService";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { useStatusSyncOnChange } from "../hooks/useStatusSyncOnChange";

type RfpFileData = {
  file?: File;
  name: string;
  size: number;
  id: number | undefined;
  datasetId: number | undefined;
  sdrId: number | undefined;
  uploadProgress: number;
  processingProgress: number;
  uploadStatus: UploadStatus;
  processingStatus: ProcessingStatusEnum;
  error?: string;
  awaitingArtifact?: "finalizing" | "failed" | false;
};

interface InputDocumentsUploaderProps {
  /** The product's run store hook (built on the workflow core slice). */
  store: WorkflowRunStoreHandle;
  /** Backend product slug, sent as `conversation.product` when an upload has to pre-create the dialogue. */
  productKey: string;
  /** Frozen data-testid namespace (external QA contract). */
  testIdPrefix: string;
  /** Frozen data-attr analytics namespace. */
  analyticsPrefix: string;
  /** Product wording for the upload help tooltip. */
  uploadTooltip: string;
  /** Hide the dataset/artifact pickers: the splash takes direct uploads only. */
  uploadOnly?: boolean;
}

const InputDocumentsUploader = ({
  store,
  productKey,
  testIdPrefix,
  analyticsPrefix,
  uploadTooltip,
  uploadOnly = false,
}: InputDocumentsUploaderProps) => {
  const {
    setInputFiles,
    dialogueId,
    selectedArtifacts,
    setSelectedArtifacts,
    setRemoveUploadedFileFunction,
  } = store();

  // Input Documents: two-step flow modals
  const [isSearchDatasetModalOpen, setIsSearchDatasetModalOpen] =
    useState(false);
  const [isArtifactModalOpen, setIsArtifactModalOpen] = useState(false);
  const [inputSearchDatasets, setInputSearchDatasets] = useState<
    ArtifactSelectorDataset[]
  >([]);

  const history = useHistory();
  const { config: chatConfig } = useChatStore();
  const { mutateAsync: createDialogue } = useCreateDialogue();

  const selectedArtifactSdrIds = useMemo(
    () =>
      Array.from(
        new Set(
          selectedArtifacts
            .map((artifact) => artifact.sdr_id)
            .filter((id): id is number => typeof id === "number"),
        ),
      ),
    [selectedArtifacts],
  );

  const { data: processingStatuses = [] } = useProcessingStatusBySdrIds(
    selectedArtifactSdrIds,
    selectedArtifactSdrIds.length > 0,
  );

  // When a file is uploaded ad-hoc, we must have a dialogue
  // in order to add the file to the dialogue's dataset.
  const handleFileSelection = useCallback(async () => {
    const { getOrCreateDialogue } = store.getState();

    try {
      const newDialogueId = await getOrCreateDialogue(async () => {
        const conversation = {
          model_id: chatConfig?.modelId ?? null,
          model_version_number: chatConfig?.modelVersionNum ?? null,
          model_inference_data: {
            infer_data: JSON.stringify([]),
            stream: false,
            text_gen_model_params: chatConfig?.chatControlMetadata || {},
          },
          dataset_id: null,
          activity_id: null,
          search_enhanced: false,
          search_datasets: chatConfig?.datasets?.map((d) => d.id) || [],
          n_search_results: chatConfig?.topSearchResults || 5,
          tool_calling: chatConfig?.toolCalling || false,
          chat_config_id: chatConfig?.chatConfigId || null,
          product: productKey,
        };

        const id = await createDialogue({ conversation });
        return id;
      });

      updateContextId(newDialogueId);

      history.replace({
        ...history.location,
        // MERGE into the existing location state — overwriting it wiped
        // sibling state (the comparison splash's pre-selected partAId/partBId),
        // so uploading a document silently cleared the chosen parts.
        state: {
          // Spread only a real object — location.state is untyped and a
          // primitive here would spread into garbage keys.
          ...(typeof history.location.state === "object" &&
          history.location.state !== null
            ? history.location.state
            : {}),
          initialUploadDialogueId: newDialogueId,
        },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      NucToast({
        message: `Failed to prepare upload: ${errorMessage}. Please check your connection and try again.`,
        type: "error",
        hideProgressBar: false,
      });
    }
  }, [chatConfig, createDialogue, history, store, productKey]);

  // Version counter to detect and discard stale updateRfpFilesStore async calls.
  // Each invocation increments the counter; if the counter has moved on by the
  // time an async gap completes, the stale call bails out without calling setInputFiles.
  const updateRfpFilesVersion = useRef(0);

  // Pending retry timeout IDs — cleared on unmount to prevent post-navigation
  // network requests and stale store writes.
  const retryTimeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  // sdrIds that already have an active retry loop — prevents duplicate loops
  // from spawning when onFilesChange fires rapidly (e.g. during progress updates).
  const retryingSdrIdsRef = useRef<Set<number>>(new Set());

  // Guard against store writes after unmount (in-flight async fetches can
  // resolve after the component is torn down).
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      retryTimeoutIdsRef.current.forEach(clearTimeout);
    };
  }, []);

  // Fast retry loop for files whose artifact ID isn't immediately available after
  // processing completes (backend race: processing status returns "completed" before
  // the artifact record is queryable).
  //
  // Uses a self-scheduling setTimeout rather than setInterval so each tick only
  // starts after the previous async fetch completes, preventing overlapping calls
  // and racing store writes under slow network conditions.
  const retryPendingArtifacts = useCallback(
    (pendingFiles: Array<{ sdrId: number; name: string }>) => {
      const MAX_ATTEMPTS = 10;
      const RETRY_INTERVAL_MS = 1500;

      // Skip any sdrIds that already have an active loop — avoids redundant API
      // calls and racing store writes when onFilesChange fires on every progress tick.
      const newFiles = pendingFiles.filter(
        (f) => !retryingSdrIdsRef.current.has(f.sdrId),
      );
      if (newFiles.length === 0) return;

      newFiles.forEach((f) => retryingSdrIdsRef.current.add(f.sdrId));
      let remaining = [...newFiles];
      let attempts = 0;

      const scheduleTick = () => {
        const timeoutId = setTimeout(async () => {
          retryTimeoutIdsRef.current.delete(timeoutId);
          attempts++;

          const fetches = remaining.map(async ({ sdrId, name }) => {
            try {
              const artifacts = await getArtifactsBySdrId(sdrId);
              if (artifacts?.length > 0) {
                return { sdrId, artifactId: artifacts[0].id as number };
              }
            } catch (err) {
              console.warn(
                `[InputDocumentsUploader] Retry artifact fetch failed for SDR ${sdrId} (${name}):`,
                err,
              );
            }
            return null;
          });

          const results = await Promise.allSettled(fetches);
          const resolved = results
            .filter((r) => r.status === "fulfilled" && r.value !== null)
            .map(
              (r) =>
                (
                  r as PromiseFulfilledResult<{
                    sdrId: number;
                    artifactId: number;
                  }>
                ).value,
            );

          // Don't write to the store if the component has unmounted
          if (!isMountedRef.current) {
            remaining.forEach((f) => retryingSdrIdsRef.current.delete(f.sdrId));
            return;
          }

          if (resolved.length > 0) {
            const artifactMap = new Map(
              resolved.map((r) => [r.sdrId, r.artifactId]),
            );
            const {
              inputFiles: currentFiles,
              setInputFiles: storeSetRfpFiles,
            } = store.getState();
            // Patch id and awaitingArtifact
            storeSetRfpFiles(
              currentFiles.map((f) =>
                f.sdrId && artifactMap.has(f.sdrId)
                  ? {
                      ...f,
                      id: artifactMap.get(f.sdrId),
                      awaitingArtifact: false,
                    }
                  : f,
              ),
            );
            resolved.forEach((r) => retryingSdrIdsRef.current.delete(r.sdrId));
            remaining = remaining.filter((f) => !artifactMap.has(f.sdrId));
          }

          if (remaining.length === 0) return;

          if (attempts >= MAX_ATTEMPTS) {
            remaining.forEach((f) => retryingSdrIdsRef.current.delete(f.sdrId));
            // Mark files as failed so AttachmentListItem can show an error state
            const {
              inputFiles: currentFiles,
              setInputFiles: storeSetRfpFiles,
            } = store.getState();
            const failedSdrIds = new Set(remaining.map((f) => f.sdrId));
            storeSetRfpFiles(
              currentFiles.map((f) =>
                f.sdrId && failedSdrIds.has(f.sdrId)
                  ? { ...f, awaitingArtifact: "failed" }
                  : f,
              ),
            );
            console.error(
              `[InputDocumentsUploader] Gave up waiting for artifact IDs after ${MAX_ATTEMPTS} retries:`,
              remaining.map((f) => f.name),
            );
            return;
          }

          scheduleTick();
        }, RETRY_INTERVAL_MS);

        retryTimeoutIdsRef.current.add(timeoutId);
      };

      scheduleTick();
    },
    [], // stable: reads store via getState() at call time; getArtifactsBySdrId is a stable import
  );

  // Helper to update the proposal generation store with all files (uploaded + selected)
  // and fetch missing artifact IDs for files that are done processing
  const updateRfpFilesStore = useCallback(
    async (
      uploadedFiles: FileUploadState[],
      artifactProcessingStatuses: ProcessingStatus[] = [],
    ) => {
      // Capture version at call time so we can detect if a newer call has been
      // made by the time any async operation completes.
      const version = ++updateRfpFilesVersion.current;

      // Always read from the store to avoid stale closures.
      const {
        selectedArtifacts: currentArtifacts,
        inputFiles: storedRfpFiles,
      } = store.getState();

      // Build a lookup of already-fetched artifact IDs keyed by sdrId.
      const storedArtifactIdBySdrId = new Map<number, number>(
        storedRfpFiles
          .filter((f) => f.sdrId && f.id)
          .map((f) => [f.sdrId!, f.id!]),
      );

      let allFiles: RfpFileData[] = [
        // Uploaded files - extract relevant info.
        ...uploadedFiles.map((file) => ({
          file: file.file,
          name: file.file.name,
          size: file.file.size,
          id:
            (file.artifactId as number | undefined) ??
            (file.sourceDataRecordId
              ? storedArtifactIdBySdrId.get(file.sourceDataRecordId)
              : undefined),
          datasetId: file.datasetId,
          sdrId: file.sourceDataRecordId,
          hookFileId: file.id,
          uploadProgress: file.uploadProgress,
          processingProgress: file.processingProgress,
          uploadStatus: file.uploadStatus as UploadStatus,
          processingStatus: file.processingStatus as ProcessingStatusEnum,
          error: file.error,
        })),
        // Selected artifacts - already enriched with dataset info from ArtifactSelectorModal
        ...currentArtifacts.map((artifact) => ({
          name: cleanArtifactFilename(artifact.filename),
          size: artifact.filesize || 0,
          id: artifact.id,
          datasetId: artifact.dataset_id,
          sdrId: artifact.sdr_id,
          uploadProgress: 100,
          uploadStatus: "uploaded" as const,
          ...selectedArtifactProcessingState(
            artifact.sdr_id,
            artifactProcessingStatuses,
          ),
        })),
      ];

      // Fetch artifact IDs as soon as sdrId is available (artifact record exists
      // from the moment of upload, so we don't need to wait for processing to complete).
      const filesNeedingArtifactIds = allFiles.filter(
        (f) => f.uploadStatus === "uploaded" && f.sdrId && !f.id,
      );

      if (filesNeedingArtifactIds.length > 0) {
        // Fetch artifact IDs in parallel for all files that need them
        const artifactFetches = filesNeedingArtifactIds.map(async (file) => {
          try {
            const artifacts = await getArtifactsBySdrId(file.sdrId!);
            if (artifacts && artifacts.length > 0) {
              const artifactId = artifacts[0].id;
              return { sdrId: file.sdrId, artifactId };
            } else {
              return null;
            }
          } catch (err) {
            console.error(
              `[InputDocumentsUploader] Failed to fetch artifact for SDR ${file.sdrId}:`,
              err,
            );
            return null;
          }
        });

        const results = await Promise.allSettled(artifactFetches);
        const artifactMap = new Map(
          results
            .filter((r) => r.status === "fulfilled")
            .map((r) => (r as any).value)
            .filter((r) => r !== null)
            .map((r) => [r.sdrId, r.artifactId]),
        );

        // Discard results if unmounted or if a newer call has been made
        if (
          !isMountedRef.current ||
          version !== updateRfpFilesVersion.current
        ) {
          return;
        }

        // Build a set of sdrIds that failed to fetch, for marking awaitingArtifact
        const failedSdrIds = new Set(
          filesNeedingArtifactIds
            .filter((f) => !artifactMap.has(f.sdrId!))
            .map((f) => f.sdrId!),
        );

        // Update files with fetched artifact IDs; mark failed ones as awaitingArtifact.
        allFiles = allFiles.map((f) => {
          if (f.sdrId && artifactMap.has(f.sdrId)) {
            return { ...f, id: artifactMap.get(f.sdrId) };
          }
          if (f.sdrId && failedSdrIds.has(f.sdrId)) {
            return { ...f, awaitingArtifact: "finalizing" };
          }
          return f;
        });

        // If any files still don't have artifact IDs after fetch, start retry loop.
        if (failedSdrIds.size > 0) {
          const stillNeeding = filesNeedingArtifactIds.filter((f) =>
            failedSdrIds.has(f.sdrId!),
          );
          console.warn(
            `[InputDocumentsUploader] Artifact IDs not yet available, starting retry:`,
            stillNeeding.map((f) => ({ name: f.name, sdrId: f.sdrId })),
          );
          retryPendingArtifacts(
            stillNeeding.map((f) => ({ sdrId: f.sdrId!, name: f.name })),
          );
        }
      }

      setInputFiles(allFiles);

      // Set sourceDataRecordIds for document-scoped search
      const { setSourceDataRecordIds: storeSetSourceDataRecordIds } =
        useChatStore.getState();

      const sdrIds = allFiles
        .filter((file) => file.sdrId)
        .map((file) => file.sdrId as number);

      if (sdrIds.length > 0) {
        storeSetSourceDataRecordIds(sdrIds);
      } else {
        storeSetSourceDataRecordIds([]);
      }
    },
    [setInputFiles, retryPendingArtifacts],
  );

  // Use the file upload hook - this handles all upload and processing status tracking
  const {
    files: uploadedFiles,
    removeFile,
    updateContextId,
    isDragActive,
    getRootProps,
    getInputProps,
    openFileDialog,
  } = useFileUploadWithProgress({
    context: "dialogue",
    contextId: "temp",
    maxFiles: 20,
    maxFileSize: Infinity,
    acceptedFileTypes: ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT,
    allowMultiple: true,
    uploadConcurrency: "sequential",
    autoStartUpload: true,
    showFileList: false,
    showIndividualProgress: true,
    showOverallProgress: false,
    onFilesSelected: handleFileSelection,
    onFilesChange: (files) => {
      updateRfpFilesStore(files, processingStatuses);
    },
    onProcessingComplete: async (file) => {
      console.log(
        `[InputDocumentsUploader] Processing complete for file: ${file.file.name}, sourceDataRecordId: ${file.sourceDataRecordId}, artifactId: ${file.artifactId}`,
      );
    },
    onError: (file, error) => {
      NucToast({
        message: `Error uploading ${file.file.name}: ${error}`,
        type: "error",
        hideProgressBar: false,
      });
    },
  });

  // Expose the hook's removeFile to the store so RFPViewer can call it.
  const removeFileRef = useRef(removeFile);
  useEffect(() => {
    removeFileRef.current = removeFile;
  });

  useEffect(() => {
    setRemoveUploadedFileFunction(async (fileId: string) => {
      await removeFileRef.current(fileId);
    });
    return () => setRemoveUploadedFileFunction(null);
  }, [setRemoveUploadedFileFunction]);

  // Sync upload context when dialogue already exists (e.g., from Generate Report or navigation)
  useEffect(() => {
    if (dialogueId) {
      updateContextId(dialogueId);
    } else {
      updateContextId("temp");
    }
  }, [dialogueId, updateContextId]);

  const syncSelectedStatuses = useCallback(
    (statuses: ProcessingStatus[]) =>
      updateRfpFilesStore(uploadedFiles, statuses),
    [uploadedFiles, updateRfpFilesStore],
  );
  useStatusSyncOnChange(
    selectedArtifactSdrIds,
    processingStatuses,
    syncSelectedStatuses,
  );

  // Step 1 of Input Documents two-step flow: datasets selected for searching
  const handleInputSearchDatasetsConfirm = useCallback(
    (datasets: ArtifactSelectorDataset[]) => {
      setInputSearchDatasets(datasets);
      setIsSearchDatasetModalOpen(false);
      setIsArtifactModalOpen(true);
    },
    [],
  );

  // Step 2 of Input Documents two-step flow: artifacts selected from search datasets
  const handleArtifactsSelect = useCallback(
    (newArtifacts: EnrichedArtifact[]) => {
      // Merge new artifacts with existing, avoiding duplicates
      const existingIds = new Set(selectedArtifacts.map((a) => a.id));
      const uniqueNewArtifacts = newArtifacts.filter(
        (a) => !existingIds.has(a.id),
      );

      const updatedArtifacts = [...selectedArtifacts, ...uniqueNewArtifacts];
      setSelectedArtifacts(updatedArtifacts);
      updateRfpFilesStore(uploadedFiles, processingStatuses);
    },
    [selectedArtifacts, uploadedFiles, processingStatuses, updateRfpFilesStore],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: 200,
      }}
    >
      {/* Input Documents Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {!uploadOnly && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<InsertDriveFileIcon />}
              onClick={() => setIsSearchDatasetModalOpen(true)}
              sx={{ textTransform: "none" }}
              data-attr={`${analyticsPrefix}:btn:select-input-files`}
              data-testid={`${testIdPrefix}-btn-select-files`}
            >
              Select Files from Datasets
            </Button>
          </Box>
        )}

        {/* Upload section */}
        <Box
          {...getRootProps()}
          data-testid={`${testIdPrefix}-btn-upload-input`}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            width: "100%",
            backgroundColor: isDragActive
              ? "action.hover"
              : "background.default",
            transition: "all 0.2s",
            cursor: "pointer",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "action.hover",
            },
          }}
        >
          <input
            {...getInputProps()}
            data-testid={`${testIdPrefix}-input-file-hidden`}
          />
          <CloudUploadIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="body1">
              {isDragActive ? "Drop files here..." : "Upload Input Document(s)"}
            </Typography>
            {!isDragActive && (
              <Tooltip title={uploadTooltip}>
                <IconButton
                  size="small"
                  aria-label="Upload documents info"
                  sx={{ p: 0.25 }}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Supported formats: PDF, DOCX, DOC, TXT
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
            data-attr={`${analyticsPrefix}:btn:upload-input-document`}
            data-testid={`${testIdPrefix}-btn-choose-file`}
          >
            Choose File
          </Button>
        </Box>
      </Box>

      {/* Input Documents – Step 1: Search Dataset Selector Modal */}
      {!uploadOnly && (
        <DatasetSelectorModal
          open={isSearchDatasetModalOpen}
          onClose={() => setIsSearchDatasetModalOpen(false)}
          onConfirm={handleInputSearchDatasetsConfirm}
          defaultSelectedDatasets={inputSearchDatasets}
        />
      )}

      {/* Input Documents – Step 2: Artifact Selector Modal */}
      {!uploadOnly && (
        <ArtifactSelectorModal
          open={isArtifactModalOpen}
          onClose={() => setIsArtifactModalOpen(false)}
          onConfirm={handleArtifactsSelect}
          datasets={inputSearchDatasets}
          defaultSelectedArtifacts={selectedArtifacts}
        />
      )}
    </Box>
  );
};

export default InputDocumentsUploader;
